<template>
  <div class="merchant-alert-page">
    <Card>
      <p slot="title">点餐提醒音配置</p>
      <Alert show-icon>
        在此上传并试听提示音。保存后：<strong>管理后台</strong>登录期间自动轮询新单/取消并播放；
        <strong>店员小程序</strong>打开前台时拉取相同音频 URL 播放。网页需保持登录且不要静音标签页。
      </Alert>

      <Form :label-width="140" class="m-t-16">
        <FormItem label="管理端网页提醒">
          <i-switch v-model="form.web_enabled" />
        </FormItem>
        <FormItem label="店员小程序铃声">
          <i-switch v-model="form.miniapp_enabled" />
        </FormItem>
        <FormItem label="定时检查间隔(秒)">
          <InputNumber v-model="form.repeat_remind_interval_sec" :min="60" :max="600" />
          <div class="hint">新单/取消申请由 WebSocket 即时提醒一次；每隔该时间再检查是否有待确认订单或待审取消，有则各响铃 1 次（默认 180 秒）</div>
        </FormItem>

        <FormItem label="新订单提示音">
          <div class="sound-row">
            <Input v-model="form.new_order_sound_path" readonly style="max-width: 420px" />
            <Upload
              :before-upload="(f) => beforeUpload(f, 'new_order')"
              :show-upload-list="false"
              accept=".mp3,.wav,.m4a,.aac"
            >
              <Button :loading="uploadingNew">上传</Button>
            </Upload>
            <Button @click="testSound('new')">试听</Button>
          </div>
          <div v-if="config.new_order_sound" class="hint">当前：{{ config.new_order_sound }}</div>
        </FormItem>

        <FormItem label="取消申请提示音">
          <div class="sound-row">
            <Input v-model="form.cancel_sound_path" readonly style="max-width: 420px" />
            <Upload
              :before-upload="(f) => beforeUpload(f, 'cancel')"
              :show-upload-list="false"
              accept=".mp3,.wav,.m4a,.aac"
            >
              <Button :loading="uploadingCancel">上传</Button>
            </Upload>
            <Button @click="testSound('cancel')">试听</Button>
          </div>
          <div v-if="config.cancel_sound" class="hint">当前：{{ config.cancel_sound }}</div>
        </FormItem>

        <FormItem>
          <Button type="primary" :loading="saving" @click="save">保存设置</Button>
          <Button class="m-l-10" @click="primeWeb">解锁网页自动播放</Button>
          <Button class="m-l-10" :loading="loading" @click="load">刷新</Button>
        </FormItem>
      </Form>
    </Card>
  </div>
</template>

<script>
import {
  getMerchantAlertConfig,
  saveMerchantAlertConfig,
  uploadMerchantAlertSound,
} from '@/api/merchantAlert'
import {
  primeMerchantWebAudio,
  isMerchantWebAudioUnlocked,
  reloadMerchantWebAlertConfig,
  startMerchantWebAlertPlayer,
} from '@/utils/merchant-alert-player'

export default {
  name: 'MerchantAlertSettings',
  data() {
    return {
      loading: false,
      saving: false,
      uploadingNew: false,
      uploadingCancel: false,
      config: {},
      form: {
        web_enabled: true,
        miniapp_enabled: true,
        repeat_remind_interval_sec: 180,
        new_order_sound_path: '',
        cancel_sound_path: '',
      },
    }
  },
  mounted() {
    this.load()
    primeMerchantWebAudio()
  },
  methods: {
    load() {
      this.loading = true
      getMerchantAlertConfig()
        .then((res) => {
          this.loading = false
          if (res.data && res.data.code === 200) {
            this.applyConfig(res.data.data)
          } else {
            this.$Message.error((res.data && res.data.msg) || '加载失败')
          }
        })
        .catch(() => {
          this.loading = false
          this.$Message.error('加载失败')
        })
    },
    applyConfig(data) {
      this.config = data || {}
      this.form.web_enabled = !!data.web_enabled
      this.form.miniapp_enabled = !!data.miniapp_enabled
      this.form.repeat_remind_interval_sec =
        Number(data.repeat_remind_interval_sec) || Number(data.poll_interval_sec) || 180
      this.form.new_order_sound_path = data.new_order_sound_path || ''
      this.form.cancel_sound_path = data.cancel_sound_path || ''
    },
    save() {
      this.saving = true
      saveMerchantAlertConfig({
        web_enabled: this.form.web_enabled,
        miniapp_enabled: this.form.miniapp_enabled,
        repeat_remind_interval_sec: this.form.repeat_remind_interval_sec,
      })
        .then((res) => {
          this.saving = false
          if (res.data && res.data.code === 200) {
            this.$Message.success('已保存')
            this.applyConfig(res.data.data)
            reloadMerchantWebAlertConfig()
            startMerchantWebAlertPlayer()
          } else {
            this.$Message.error((res.data && res.data.msg) || '保存失败')
          }
        })
        .catch(() => {
          this.saving = false
          this.$Message.error('保存失败')
        })
    },
    beforeUpload(file, kind) {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('kind', kind === 'cancel' ? 'cancel' : 'new_order')
      const loadingKey = kind === 'cancel' ? 'uploadingCancel' : 'uploadingNew'
      this[loadingKey] = true
      uploadMerchantAlertSound(fd, kind === 'cancel' ? 'cancel' : 'new_order')
        .then((res) => {
          this[loadingKey] = false
          if (res.data && res.data.code === 200) {
            this.$Message.success('上传成功')
            const cfg = (res.data.data && res.data.data.config) || res.data.data
            if (cfg) this.applyConfig(cfg)
            reloadMerchantWebAlertConfig()
          } else {
            const msg = (res.data && res.data.msg) || '上传失败'
            this.$Message.error(msg)
            if (res.data && res.data.code === 502) {
              this.$Message.warning('登录可能已过期，请重新登录后再上传')
            }
          }
        })
        .catch((err) => {
          this[loadingKey] = false
          this.$Message.error(typeof err === 'string' ? err : '上传失败')
        })
      return false
    },
    testSound(kind) {
      const url =
        kind === 'cancel' ? this.config.cancel_sound : this.config.new_order_sound
      if (!url) {
        this.$Message.warning('请先上传音频')
        return
      }
      primeMerchantWebAudio()
      const a = new Audio(url)
      a.play().catch(() => {
        this.$Message.warning('播放失败，请先点「解锁网页自动播放」')
      })
    },
    primeWeb() {
      primeMerchantWebAudio()
      const tip = isMerchantWebAudioUnlocked()
        ? '本浏览器标签页已解锁，关闭标签页后需再点一次'
        : '已解锁，后续新单将尝试自动播放'
      this.$Message.success(tip)
      startMerchantWebAlertPlayer()
    },
  },
}
</script>

<style scoped>
.merchant-alert-page {
  padding: 0 8px 24px;
}
.sound-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.hint {
  margin-top: 8px;
  font-size: 12px;
  color: #888;
  word-break: break-all;
}
.m-t-16 {
  margin-top: 16px;
}
.m-l-10 {
  margin-left: 10px;
}
</style>
